import { useCallback, useMemo } from "react";
import { useGetFormsAndAssessmentsByAppointment } from "@/queries/patient-chart/forms/use-get-forms-and-assessments-by-appointment";
import { usePatientIntakeFormList } from "@/queries/patient-portal/forms/use-patient-intake-form-list";
import { PatientIntakeForm } from "@/services";
import { DEF_PAGINATION, getXtenantId } from "@/utils/utils";
import { APPOINTMENT_FORM_GROUPS } from "../appointment-additional-forms-constant";
import {
  AppointmentFormGroup,
  FormsAndAssessmentsPayload,
  IntakePreviewData,
} from "../appointment-additional-forms-types";
import {
  findIntakePreview,
  toFormRows,
} from "../appointment-additional-forms-utils";

export interface UseAppointmentForms {
  groups: AppointmentFormGroup[];
  isLoading: boolean;
  isError: boolean;
  getIntakePreview: (sectionName: string) => IntakePreviewData | undefined;
}

export const useAppointmentForms = (
  appointmentUuid?: string
): UseAppointmentForms => {
  const xTENANTID = getXtenantId() ?? undefined;
  const formsQuery = useGetFormsAndAssessmentsByAppointment({
    appointmentUuid: appointmentUuid ?? "",
    xTENANTID,
  });
  const intakeQuery = usePatientIntakeFormList({
    appointmentUuid,
    pagination: DEF_PAGINATION,
    xTENANTID,
  });

  // The generated client types this endpoint's body as a bare `Response`.
  const payload = (
    formsQuery.data?.data as { data?: FormsAndAssessmentsPayload } | undefined
  )?.data;

  const submissions: PatientIntakeForm[] = useMemo(
    () => intakeQuery.data?.data?.content ?? [],
    [intakeQuery.data]
  );

  const groups = useMemo<AppointmentFormGroup[]>(
    () =>
      APPOINTMENT_FORM_GROUPS.map((group) => ({
        ...group,
        rows: toFormRows(payload?.patientForms, group.kind, submissions),
      })),
    [payload, submissions]
  );

  const getIntakePreview = useCallback(
    (sectionName: string) => findIntakePreview(submissions, sectionName),
    [submissions]
  );

  return {
    groups,
    isLoading: formsQuery.isLoading || intakeQuery.isLoading,
    isError: formsQuery.isError,
    getIntakePreview,
  };
};
